/** Parse and map pasted / CSV table text onto KB column fields. */

export function normaliseHeader(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
}

function parseDelimitedLine(line, delimiter) {
  const cells = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"'
          i += 1
        } else {
          inQuotes = false
        }
      } else {
        current += ch
      }
      continue
    }
    if (ch === '"') {
      inQuotes = true
      continue
    }
    if (ch === delimiter) {
      cells.push(current)
      current = ''
      continue
    }
    current += ch
  }
  cells.push(current)
  return cells.map((c) => c.trim())
}

function detectDelimiter(headerLine) {
  const tabs = (headerLine.match(/\t/g) || []).length
  const commas = (headerLine.match(/,/g) || []).length
  const semis = (headerLine.match(/;/g) || []).length
  if (tabs >= commas && tabs >= semis && tabs > 0) return '\t'
  if (semis > commas) return ';'
  return ','
}

/**
 * Parse CSV / TSV / pasted spreadsheet text into { headers, rows }.
 * First non-empty line is treated as the header row.
 */
export function parseTableText(raw) {
  const text = String(raw || '').replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const lines = text.split('\n').filter((line) => line.trim().length > 0)
  if (lines.length < 2) {
    return { headers: [], rows: [], error: 'Need a header row and at least one data row.' }
  }

  const delimiter = detectDelimiter(lines[0])
  const headers = parseDelimitedLine(lines[0], delimiter).filter((h, idx, arr) => {
    // Keep trailing empties only if something follows — drop all-empty trailing
    return h || arr.slice(idx + 1).some((x) => x)
  })
  if (!headers.length || headers.every((h) => !h)) {
    return { headers: [], rows: [], error: 'Could not read column headers.' }
  }

  const rows = []
  for (const line of lines.slice(1)) {
    const cells = parseDelimitedLine(line, delimiter)
    while (cells.length < headers.length) cells.push('')
    const values = headers.map((_, i) => (cells[i] ?? '').trim())
    if (values.every((v) => !v)) continue
    rows.push(values)
  }

  if (!rows.length) {
    return { headers, rows: [], error: 'No data rows found under the header.' }
  }

  return { headers, rows, error: null }
}

function tokenSet(normalised) {
  return new Set(normalised.split(' ').filter(Boolean))
}

function jaccard(a, b) {
  if (!a.size || !b.size) return 0
  let inter = 0
  for (const t of a) if (b.has(t)) inter += 1
  return inter / (a.size + b.size - inter)
}

/**
 * Suggest which source header index maps to each KB column key.
 * Returns { [columnKey]: sourceHeaderIndex | null }
 */
export function suggestColumnMapping(sourceHeaders, columns) {
  const mapping = {}
  const used = new Set()
  const norms = sourceHeaders.map((h) => normaliseHeader(h))

  for (const col of columns) {
    const target = normaliseHeader(col.label)
    const targetTokens = tokenSet(target)
    let bestIdx = -1
    let bestScore = 0

    norms.forEach((src, idx) => {
      if (used.has(idx) || !src) return
      let score = 0
      if (src === target) score = 1
      else if (src.includes(target) || target.includes(src)) {
        score = 0.85
      } else {
        score = jaccard(tokenSet(src), targetTokens)
      }
      // Slight boost when slug-like forms match
      const srcCompact = src.replace(/\s/g, '')
      const tgtCompact = target.replace(/\s/g, '')
      if (srcCompact && tgtCompact && (srcCompact.includes(tgtCompact) || tgtCompact.includes(srcCompact))) {
        score = Math.max(score, 0.8)
      }
      if (score > bestScore) {
        bestScore = score
        bestIdx = idx
      }
    })

    if (bestIdx >= 0 && bestScore >= 0.45) {
      mapping[col.key] = bestIdx
      used.add(bestIdx)
    } else {
      mapping[col.key] = null
    }
  }

  return mapping
}

export function applyMapping(headers, dataRows, columns, mapping) {
  return dataRows.map((cells) => {
    const values = {}
    for (const col of columns) {
      const idx = mapping[col.key]
      values[col.key] =
        idx == null || idx < 0 || idx >= cells.length ? '' : String(cells[idx] ?? '').trim()
    }
    return { values }
  })
}
